import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useErrorToast } from '@/shared/hooks/useErrorToast';
import { API_BASE_URL } from '@/shared/lib/constants';

interface WaterQualityExportButtonProps {
  farmId: string;
  pondId: string | null;
  pondName?: string;
  disabled?: boolean;
}

export function WaterQualityExportButton({
  farmId,
  pondId,
  pondName,
  disabled = false,
}: WaterQualityExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { showError } = useErrorToast();

  const handleExport = async () => {
    if (!pondId) {
      showError(400, 'Please select a pond to export');
      return;
    }

    try {
      setIsExporting(true);

      // Build the export URL using the same API_BASE_URL as other API calls
      // This ensures we use the Vite proxy for proper CORS and cookie handling
      const exportUrl = `${API_BASE_URL}/farms/${farmId}/ponds/${pondId}/water-quality/export?limit=1000`;

      console.log('[Export] Requesting URL:', exportUrl);
      console.log('[Export] farmId:', farmId, 'pondId:', pondId);

      // Make the request (uses cookie-based authentication)
      const response = await fetch(exportUrl, {
        method: 'GET',
        credentials: 'include', // Send cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          showError(401, 'Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          showError(403, 'You do not have permission to export this data.');
        } else if (response.status === 404) {
          showError(404, 'Pond not found.');
        } else {
          showError(response.status, 'Failed to export data. Please try again.');
        }
        return;
      }

      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `water_quality_export_${pondName || 'pond'}_${new Date().toISOString().split('T')[0]}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Success - file downloaded (no toast needed as download provides feedback)
      console.log('Export completed successfully:', filename);
    } catch (error) {
      console.error('Export error:', error);
      showError(500, error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="md"
      onClick={handleExport}
      disabled={disabled || !pondId || isExporting}
      className="gap-2"
    >
      <Download className={`h-4 w-4 ${isExporting ? 'animate-bounce' : ''}`} />
      {isExporting ? 'Exporting...' : 'Export to Excel'}
    </Button>
  );
}
